import React, { useState } from 'react';
import { X } from 'lucide-react';

/**
 * STAND-IN SETTINGS for ONE room (STAND-IN.md §4, §9) — shared by the room's 🤝 popover and the Profile tab. Every
 * control is the person's OWN: the switch, the agents (only those they supervise here — several may be on; per
 * mention ONE answers, the best fit), the bar ("post if ≥ N% sure", and the bar it has EARNED), "approve plan and run
 * steps on my behalf", the track record, the answers waiting for them, and every lesson it learned (delete any).
 * ``standin`` = the ``useStandin`` value ({loaded, setting, eligible, drafts, lessons, trackRecord, away, run}).
 */
export default function StandinSettingsPanel({ standin, onOpenProfile = null, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const s = standin || {};
  const eligible = s.eligible || [];
  if (!s.loaded) return <p className="text-[11px] text-gray-400">Loading…</p>;
  if (!eligible.length) {
    return <p className="text-[11px] text-gray-500">You don&apos;t supervise an agent in this room.</p>;
  }
  const setting = s.setting || {};
  const on = !!setting.enabled;
  const chosen = (setting.agentIds && setting.agentIds.length ? setting.agentIds : [eligible[0].id]);
  const pct = Math.round(Number(setting.threshold || 0.8) * 100);
  const save = async (patch) => {
    setBusy(true); setError('');
    try {
      await s.run('set', { agentIds: chosen, enabled: on, threshold: setting.threshold || 0.8, ...patch });
    } catch (e) { setError((e && e.message) || "Couldn't save that."); }
    setBusy(false);
  };
  const toggleAgent = (id) => {
    const next = chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id];
    if (next.length) save({ agentIds: next });
  };
  const tr = s.trackRecord || {};
  const counts = [['sent', 'sent as written'], ['edited', 'changed'], ['discarded', 'discarded'], ['corrected', 'corrected'],
    ['auto', 'posted on its own']].filter(([k]) => Number(setting[k]) > 0);
  const nameOf = (id) => (eligible.find((a) => a.id === id) || {}).name || id;
  const waiting = (s.drafts || []).length;
  return (
    <div className="text-[12px]" data-testid="standin-panel">
      {s.away === false ? (
        <div data-testid="standin-paused" className="mb-2 rounded-md bg-gray-100 px-2 py-1.5 text-[11px] text-gray-700">
          Paused everywhere — &quot;Answer for me&quot; is off in your profile.
          {onOpenProfile ? <button type="button" onClick={onOpenProfile} className="ml-1 font-medium text-indigo-700 hover:underline">Open profile</button> : null}
        </div>
      ) : null}
      <label className="mb-2 flex items-center gap-2">
        <input type="checkbox" data-testid="standin-enabled" checked={on} disabled={busy}
          onChange={(e) => save({ enabled: e.target.checked })} />
        <span className="font-medium text-gray-800">Answer for me when I&apos;m mentioned here</span>
      </label>
      <div className="mb-2">
        <div className="mb-0.5 text-gray-500">Agents{eligible.length > 1 ? ' — the best fit for each question answers' : ''}</div>
        {eligible.map((a) => (
          <label key={a.id} className="flex items-center gap-2 py-0.5">
            <input type="checkbox" data-testid={`standin-agent-${a.id}`} checked={chosen.includes(a.id)} disabled={busy}
              onChange={() => toggleAgent(a.id)} />
            <span className="text-gray-800">{a.name}</span>
          </label>
        ))}
      </div>
      <label className="mb-1 flex items-center gap-2">
        <span className="w-14 shrink-0 text-gray-500">Post if</span>
        <input type="range" min="50" max="95" step="5" value={pct} disabled={busy} data-testid="standin-threshold"
          onChange={(e) => save({ threshold: Number(e.target.value) / 100 })} className="flex-1" />
        <span className="w-16 text-right tabular-nums text-gray-700">≥ {pct}% sure</span>
      </label>
      <p className="mb-2 text-[11px] text-gray-500">Below that, it still answers — marked <b>pending your confirmation</b>, so the room
        sees it as tentative until you approve, change or discard it.</p>
      {s.effectiveWhy ? (
        <p data-testid="standin-earned" className="mb-2 rounded-md bg-indigo-50 px-2 py-1 text-[11px] text-indigo-800">
          Posts at ≥ {Math.round(Number(s.effectiveThreshold || 0) * 100)}% now — {s.effectiveWhy}
        </p>
      ) : null}
      <label className="mb-2 flex items-start gap-2">
        <input type="checkbox" data-testid="standin-auto-approve" checked={!!setting.autoApprove} disabled={busy || !on}
          onChange={(e) => save({ autoApprove: e.target.checked })} className="mt-0.5" />
        <span className="text-gray-700">
          <span className="font-medium text-gray-800">Approve plan and run steps on my behalf when it&apos;s sure</span>
          <span className="block text-[11px] text-gray-500">It always shows its recommendation on the step. It only ever approves — never denies or changes — and never when someone is already reviewing it.</span>
        </span>
      </label>
      {tr.reviewed ? (
        <div data-testid="standin-track" className="mb-2 rounded-md bg-gray-50 px-2 py-1.5 text-[11px] text-gray-600">
          You approved {Math.round((tr.acceptedRate || 0) * 100)}% as written
          {counts.length ? ` · ${counts.map(([k, l]) => `${setting[k]} ${l}`).join(' · ')}` : ''}
        </div>
      ) : null}
      {waiting ? (
        <div data-testid="standin-waiting" className="mb-2 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
          {waiting} answer{waiting === 1 ? '' : 's'} waiting for your confirmation in this room.
        </div>
      ) : null}
      {compact ? null : (<>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">What it learned from you</div>
        {(s.lessons || []).length ? (
          <ul data-testid="standin-lessons" className="max-h-40 space-y-1 overflow-y-auto">
            {s.lessons.map((l) => (
              <li key={`${l.agentId}|${l.trigger}`} className="flex items-start gap-1.5 rounded px-1 py-0.5 hover:bg-gray-50">
                <span className="min-w-0 flex-1 text-[11px] leading-snug text-gray-700">
                  {chosen.length > 1 ? <span className="mr-1 rounded bg-gray-100 px-1 text-gray-500">{nameOf(l.agentId)}</span> : null}
                  <span className="text-gray-400">When</span> {l.trigger} <span className="text-gray-400">→</span> {l.fix}
                  {l.count > 1 ? <span className="ml-1 text-gray-400">×{l.count}</span> : null}
                </span>
                <button type="button" aria-label="Forget this" title="Forget this" data-testid={`standin-forget-${l.trigger}`}
                  onClick={() => s.run('forget', { agentId: l.agentId || chosen[0], trigger: l.trigger }).catch(() => {})}
                  className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700">
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="text-[11px] text-gray-400">Nothing yet — when you change or correct an answer, it remembers.</p>}
      </>)}
      {error ? <p className="mt-2 text-[11px] text-rose-600" data-testid="standin-error">{error}</p> : null}
    </div>
  );
}
